import { z } from 'zod'

export const multiChoiceQuestionOptionSchema = z.object({
  label: z.string(),
  value: z
    .number()
    .describe(
      'Numeric value for ranking the answer. Must be between 0 and 1. Scores closer to 1 are considered "better". If left unset, the inverse of the choice index will be used and normalized to a 0..1 range.'
    ),
})

export const multiChoiceQuestionConfigSchema = z.object({
  options: z.array(multiChoiceQuestionOptionSchema),
})

export const questionnaireQuestionId = z.union([z.literal('timing'), z.literal('sales_status')])
export type QuestionnaireQuestionId = z.infer<typeof questionnaireQuestionId>

export const questionConditionSchema = z.object({
  id: questionnaireQuestionId,
  value: z.number(),
})
export type QuestionCondition = z.infer<typeof questionConditionSchema>

const questionBaseSchema = z.object({
  id: questionnaireQuestionId,
  title: z.string().optional(),
  question: z.string(),
  description: z.string().optional(),
  weight: z
    .number()
    .default(1)
    .optional()
    .describe(
      'Weight of the question in the questionnaire, defining how important the score of this question is to the overall score of the questionnaire.'
    ),
  order: z.number().default(0).describe('Order of the question in the questionnaire.'),
  conditions: z
    .array(questionConditionSchema)
    .optional()
    .describe(
      'Array of conditions that must be met for this question to be displayed. Uses OR logic - if any condition matches, the question is shown.'
    ),
})

// Schema for multi-choice questions
export const multiChoiceQuestionSchema = z.object({
  type: z.literal('multi-choice'),
  config: multiChoiceQuestionConfigSchema,
})

// Schema for text input questions
export const textInputQuestionSchema = z.object({
  type: z.literal('text-input'),
  config: z.object({
    placeholder: z.string().optional(),
    multiline: z.boolean().default(false).optional(),
  }),
})

// Union schema covering all supported question types
export const questionSchema = questionBaseSchema.and(
  z.discriminatedUnion('type', [multiChoiceQuestionSchema, textInputQuestionSchema])
)

export const questionnaireSchema = z.object({
  name: z.string(),
  questions: z.array(questionSchema),
})

export type QuestionnaireData = z.infer<typeof questionnaireSchema>
export type Question = z.infer<typeof questionSchema>
export type MultiChoiceQuestionConfig = z.infer<typeof multiChoiceQuestionConfigSchema>
export type MultiChoiceQuestionOption = z.infer<typeof multiChoiceQuestionOptionSchema>
export type TextInputQuestionConfig = z.infer<typeof textInputQuestionSchema>

export const questionnaireAnswerValueSchema = z.union([z.string(), z.number()])

export const questionnaireAnswerSchema = z.object({
  id: z.string(),
  value: questionnaireAnswerValueSchema,
})

export const questionnaireAnswersSchema = z.array(questionnaireAnswerSchema)

export type RespondedQuestionnaireAnswers = z.infer<typeof questionnaireAnswersSchema>

export const declinedQuestionnaireAnswersSchema = z.object({
  declined: z.union([z.date(), z.string()]),
})

export type DeclinedQuestionnaireAnswers = z.infer<typeof declinedQuestionnaireAnswersSchema>

export const inactiveQuestionnaireAnswersSchema = z.object({
  inactive: z.union([z.date(), z.string()]),
})

export type InactiveQuestionnaireAnswers = z.infer<typeof inactiveQuestionnaireAnswersSchema>

export function isDeclinedQuestionnaireAnswers(
  answers: QuestionnaireAnswers
): answers is DeclinedQuestionnaireAnswers {
  return typeof answers === 'object' && 'declined' in answers
}

export function isInactiveQuestionnaireAnswers(
  answers: QuestionnaireAnswers
): answers is InactiveQuestionnaireAnswers {
  return typeof answers === 'object' && 'inactive' in answers
}

export const questionnaireAnswersWithDeclinedSchema = z.union([
  questionnaireAnswersSchema,
  declinedQuestionnaireAnswersSchema,
  inactiveQuestionnaireAnswersSchema,
])

export type QuestionnaireAnswers = z.infer<typeof questionnaireAnswersWithDeclinedSchema>

export function getRequiredQuestionIdsForFeedback(): QuestionnaireQuestionId[] {
  return ['timing', 'sales_status']
}

export function hasRequiredQuestionnaireAnswers({
  answers,
}: {
  answers: RespondedQuestionnaireAnswers | null | undefined
}): boolean {
  if (!answers || answers.length === 0) {
    return false
  }

  const requiredQuestionIds = getRequiredQuestionIdsForFeedback()

  return requiredQuestionIds.every((requiredQuestionId) => {
    const matchingAnswer = answers.find((answerEntry) => answerEntry.id === requiredQuestionId)
    const answerValue = matchingAnswer?.value

    if (typeof answerValue === 'number') {
      return true
    }

    if (typeof answerValue === 'string') {
      return answerValue.trim().length > 0
    }

    return false
  })
}

export function isQuestionnaireAnswers(
  answers: QuestionnaireAnswers
): answers is RespondedQuestionnaireAnswers {
  return questionnaireAnswersSchema.safeParse(answers).success
}
